export type AttachmentsFolder = {
  id: string
  folder: string
  files: number
  url: string
  jobId?: number
}

export type Attachment = {
  id: string
  name: string
  size: string
  filename?: string
  sizeInBytes?: number
  url: string
  icon: string
  dateTime: string
  tags: string
  folder: string
  thumbnail: string | null
  path: string
  jobId?: number
}

export type UpdateAttachmentData = {
  id: string
  name: string
  tags: string
  folder: string
  jobId?: number
}

type NewAttachmentData = {
  name?: string
  customerId: number
  propertyId?: number
  jobId?: number
  folder?: string
  tags: string
}

export type UploadRequestOptions = {
  attachment: NewAttachmentData
  file: File
  filename: string
  uid: string
  onProgress: (uid: string, percent: number) => void
  onSuccess: (uid: string) => void
  onError: (uid: string) => void
}

export type AttachmentLink = {
  attachmentId: string
  link: string
  contentType: string
  filename: string
}

export const findMatchingFolder = (folders: AttachmentsFolder[], match: string): AttachmentsFolder | undefined => {
  const matchLower = match.toLowerCase()
  return folders.find(x => x.id.toLowerCase() === match || x.folder.toLowerCase() === matchLower || x.url?.toLowerCase() === match)
}

export const filterFolders = (folders: AttachmentsFolder[], search: string): AttachmentsFolder[] => {
  const searchTerm = search.toLowerCase()
  return folders.filter(x => x.folder.toLowerCase().includes(searchTerm))
}

export const escapeFolderId = (folderId?: string) => folderId === undefined ? undefined : folderId.replace(/&/g, '__amp').replace(/\//g, "__fsl")

export const getTagList = (attachments: Attachment[]): {label: string, value: string}[] => {
  const tags: string[] = []
  let anyHaveNoTags = false

  attachments.forEach(attachment => {
    if (attachment.tags?.length < 1) {
      anyHaveNoTags = true
    }
    splitTags(attachment.tags).forEach(tag => {
      if (tag.trim().length > 0 && !tags.includes(tag)) {
        tags.push(tag)
      }
    })
  })

  tags.sort()
  const mapped = tags.map(x => ({ label: x, value: x }))

  return anyHaveNoTags
    ? [{ label: '(no tags)', value: '' }, ...mapped]
    : mapped
}

export const filterAttachmentsByTags = (attachments: Attachment[], tags: string[]): Attachment[] => {
  if (tags.length < 1) {
    return [...attachments]
  }
  
  return attachments.filter(attachment => {
    return tags.some(tag => {
      if (tag === '') {
        return !attachment.tags
      }

      return splitTags(attachment.tags).includes(tag)
    })
  })
}

export const filterAttachmentsBySearch = (attachments: Attachment[], search: string): Attachment[] => {
  const searchTerm = search.toLowerCase()
  return attachments.filter(x => x.name.toLowerCase().includes(searchTerm) || x.tags.toLowerCase().includes(searchTerm))
}

export const filterAttachmentsByFolder = (attachments: Attachment[], folder: string): Attachment[] => {
  if (folder === '') {
    return attachments
  }
  const lowerCaseFolder = folder.toLowerCase()
  return attachments.filter(x => x.folder.toLowerCase() === lowerCaseFolder)
}

export const getFolderFilterOptions = (attachments: Attachment[]): { label: string, value: string }[] => {
  const uniqueFolders = Array.from(new Set(attachments.map(x => x.folder)))
  uniqueFolders.sort()
  return [
    { label: 'All Folders', value: '' },
    ...uniqueFolders.map(x => ({ label: x, value: x }))
  ]
}

export const splitTags = (tags: string): string[] => (tags?.split(', ') ?? []).filter(x => x.trim().length > 0)

export const joinTags = (tags: string[]): string => tags.join(', ')

export const createBlankAttachment = (): Attachment => ({
  id: '',
  name: '',
  size: '',
  url: '',
  icon: '',
  dateTime: '',
  tags: '',
  folder: '',
  thumbnail: '',
  path: ''
})

export const filterForPreview = (attachments: Attachment[]): Attachment[] => {
  return attachments.filter(a => a.path.match(/.\.(bmp|gif|img|jpeg|jpg|png|tiff)$/i) && a.size !== '0 B')
}